/**
 * installPrompt.ts — 안드로이드 Chrome 계열의 `beforeinstallprompt` 를 붙잡아 두는 모듈.
 *
 * 이 이벤트는 페이지가 설치 조건을 만족하는 순간 한 번만 오고, 붙잡아 두지 않으면 브라우저 자체 배너가 뜬다.
 * React 가 마운트되기 전에 올 수 있어 `main.tsx` 가 이 모듈을 먼저 import 한다(부수효과로 리스너 등록).
 * 아이폰 Safari 에는 이 이벤트가 없다(안내 그림만 가능).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallPromptState {
  /** 지금 시스템 설치창을 띄울 수 있는지(이벤트를 붙잡아 둔 상태) */
  available: boolean;
  /** 이 세션에서 설치가 끝났는지(`appinstalled`) */
  installed: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallPromptState = { available: false, installed: false };
const listeners = new Set<() => void>();

function emit(next: InstallPromptState) {
  state = next;
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // 브라우저 기본 배너 대신 우리 모달에서 띄운다
    deferred = e as BeforeInstallPromptEvent;
    emit({ ...state, available: true });
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    emit({ available: false, installed: true });
  });
}

export function subscribeInstallPrompt(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getInstallPromptState(): InstallPromptState {
  return state;
}

/** 시스템 설치창 띄우기 — 반드시 사용자 제스처(버튼) 안에서. 이벤트는 한 번 쓰면 소모된다 */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const ev = deferred;
  if (!ev) return "unavailable";
  deferred = null;
  emit({ ...state, available: false });
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    return choice.outcome;
  } catch {
    return "unavailable";
  }
}
